import React from "react";
import { Segment, Segments, Html } from "@react-three/drei";
import { Cuboid } from "../types";
import { useMemo, useState, useCallback, useRef } from "react";
import {
  BufferGeometry,
  Float32BufferAttribute,
  Matrix4,
  MeshBasicMaterial,
  Vector3,
} from "three";

function getCuboidVertices(cuboid: Cuboid): Vector3[] {
  const cx = cuboid["position.x"];
  const cy = cuboid["position.y"];
  const cz = cuboid["position.z"];
  const dx = cuboid["dimensions.x"] / 2;
  const dy = cuboid["dimensions.y"] / 2;
  const dz = cuboid["dimensions.z"] / 2;
  const yaw = cuboid.yaw;

  const corners = [
    new Vector3(-dx, -dy, -dz),
    new Vector3(dx, -dy, -dz),
    new Vector3(dx, -dy, dz),
    new Vector3(-dx, -dy, dz),
    new Vector3(-dx, dy, -dz),
    new Vector3(dx, dy, -dz),
    new Vector3(dx, dy, dz),
    new Vector3(-dx, dy, dz),
  ];

  const rotationMatrix = new Matrix4().makeRotationZ(yaw);
  corners.forEach((v) =>
    v.applyMatrix4(rotationMatrix).add(new Vector3(cx, cy, cz)),
  );
  return corners;
}

const popoverGraceTime = 800;

export function CuboidTag({ cuboid }: { cuboid: Cuboid }) {
  const [hovered, setHovered] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const [hoveringPopover, setHoveringPopover] = useState(false);

  const onOver = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setHovered(true);
  }, []);

  const onOut = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setHovered(false);
    }, popoverGraceTime);
  }, []);
  const cuboidVertices = useMemo(() => getCuboidVertices(cuboid), [cuboid]);

  const edges = useMemo(
    () => [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4],
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7],
    ],
    [cuboid],
  );

  const frontGeometry = useMemo(() => {
    const geometry = new BufferGeometry();
    const frontFace = [4, 7, 5, 6];
    const vertices = frontFace.flatMap((i) => cuboidVertices[i].toArray());
    geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
    geometry.setIndex([0, 1, 2, 1, 2, 3]);
    geometry.computeVertexNormals();
    return geometry;
  }, [cuboidVertices]);

  const frontMaterial = useMemo(
    () =>
      new MeshBasicMaterial({
        color: "#87CEEB",
        transparent: true,
        opacity: 0.5,
        side: 2,
        depthTest: false,
      }),
    [],
  );

  const tagPosition = useMemo(() => {
    return new Vector3()
      .addVectors(cuboidVertices[4], cuboidVertices[6])
      .multiplyScalar(0.5);
  }, [cuboidVertices]);

  return (
    <>
      <mesh
        geometry={frontGeometry}
        material={frontMaterial}
        onPointerOver={onOver}
        onPointerOut={onOut}
        onPointerMove={onOver}
      />

      <Html position={tagPosition}>
        {(hovered || hoveringPopover) && (
          <div
            onPointerEnter={() => setHoveringPopover(true)}
            onPointerLeave={() => {
              setHoveringPopover(false);
              timeoutRef.current = setTimeout(() => setHovered(false), 1000);
            }}
            className="cuboid-tag__popover"
          >
            {cuboid.label}
            <br />
            {cuboid.stationary ? "Stationary" : "Moving"}
          </div>
        )}
      </Html>

      <Segments>
        {edges.map(([start, end], i) => (
          <Segment
            key={i}
            start={cuboidVertices[start]}
            end={cuboidVertices[end]}
            color="#0033AA"
          />
        ))}
      </Segments>
    </>
  );
}
